// Inventory.jsx 🛡️ Dark Mode Inventory Manager - Final + Status Grouping + Default Status + Country-Based Access Control
import React, { useState, useEffect, useCallback, useRef } from "react";
import { saveAs } from "file-saver";
import { Toast, ToastContainer } from "react-bootstrap";
import axios from "axios";
import "./Inventory.css";

const implementationStatusOptions = ["Implemented", "Partially Implemented", "Under Study", "Not Implemented"];

const countryOptions = [
  { code: "BE", name: "Belgium" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "CH", name: "Switzerland" },
  { code: "US", name: "United States" },
  { code: "IT", name: "Italy" },
  { code: "IE", name: "Ireland" },
  { code: "ES", name: "Spain" },
  { code: "GR", name: "Greece" }
];

const normalizeInventoryData = (data) => {
  return data.map(item => ({
    category: item.category,
    solutionType: item.solution_type,
    solutionUsed: item.solution_used,
    provider: item.provider,
    implementationStatus: item.implementation_status || "Not Implemented",
    requirementLevel: item.requirement_level || "Unspecified"
  }));
};

export default function Inventory({ userCountryCode }) {
  const [inventory, setInventory] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(userCountryCode ? userCountryCode.toUpperCase() : "BE");
  const [toast, setToast] = useState({ show: false, message: "", variant: "success" });
  const [editableField, setEditableField] = useState(null);
  const inputRefs = useRef({});
  const debounceTimeout = useRef(null);

  const isEditableUser = selectedCountry === userCountryCode?.toUpperCase();

  const showToast = (message, variant = "success") => {
    setToast({ show: true, message, variant });
    setTimeout(() => {
      setToast(prev => ({ ...prev, show: false }));
    }, 3000);
  };

  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const response = await axios.get('/api/inventory.php', {
          params: { country: selectedCountry },
          withCredentials: true
        });

        if (response.data.success) {
          if (response.data.data.length === 0) {
            await axios.post('/api/inventory.php', {
              countryCode: selectedCountry
            }, {
              withCredentials: true,
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              }
            });

            showToast(`Inventory initialized for ${selectedCountry}`, "success");

            const secondResponse = await axios.get('/api/inventory.php', {
              params: { country: selectedCountry },
              withCredentials: true
            });

            if (secondResponse.data.success) {
              setInventory(normalizeInventoryData(secondResponse.data.data));
            }
          } else {
            setInventory(normalizeInventoryData(response.data.data));
            showToast(`Inventory loaded for ${selectedCountry}`, "success");
          }
        } else {
          showToast('Error loading inventory.', "danger");
          console.error('Error loading inventory:', response.data.error);
        }
      } catch (error) {
        showToast('Network error loading inventory.', "danger");
        console.error('Fetch inventory failed:', error);
      }
    };

    fetchInventory();
  }, [selectedCountry]);

  const saveInventoryItem = useCallback((item) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(async () => {
      try {
        await axios.post('/api/inventory.php', {
          category: item.category,
          solutionType: item.solutionType,
          solutionUsed: item.solutionUsed,
          provider: item.provider,
          implementationStatus: item.implementationStatus,
          countryCode: selectedCountry
        }, {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        });

        showToast(`Saved: ${item.category}`, "success");

      } catch (error) {
        showToast(`Error saving ${item.category}`, "danger");
        console.error(`Error saving ${item.category}:`, error);
      }
    }, 300);
  }, [selectedCountry]);

  const handleChange = (index, field, value) => {
    const updatedInventory = [...inventory];
    updatedInventory[index][field] = value;
    setInventory(updatedInventory);
    saveInventoryItem(updatedInventory[index]);
  };

  const handleExportCSV = () => {
    const headers = ["Category", "Type", "Solution Used", "Provider / Vendor", "Implementation Status"];
    const filledRows = inventory.filter(item => item.solutionUsed || item.provider || item.implementationStatus);

    if (filledRows.length === 0) {
      showToast("No filled rows to export.", "warning");
      return;
    }

    const rows = filledRows.map(item => [
      item.category.trim(),
      item.solutionType.trim(),
      (item.solutionUsed || '').trim(),
      (item.provider || '').trim(),
      (item.implementationStatus || '').trim()
    ]);

    const csvContent = headers.join(",") + "\n" + rows.map(e => e.map(field => `"${field}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `security_inventory_${selectedCountry}.csv`);
    showToast(`Exported ${rows.length} row(s) for ${selectedCountry}`, "success");
  };

  const handleCountryChange = (e) => {
    setSelectedCountry(e.target.value);
    setEditableField(null);
  };

  const unlockField = (index, field) => {
    if (!isEditableUser) return;
    setEditableField(`${index}-${field}`);
    setTimeout(() => {
      inputRefs.current[`${index}-${field}`]?.focus?.();
    }, 0);
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "Implemented":
        return "select-implemented";
      case "Partially Implemented":
        return "select-partial";
      case "Under Study":
        return "select-study";
      case "Not Implemented":
      default:
        return "select-not";
    }
  };

  const groupByRequirementLevel = (items) => {
	  const levels = ["Required", "Important", "Improvement", "Sustainability", "Unspecified"];
	  const grouped = {};
	  levels.forEach(level => grouped[level] = []);
	  items.forEach((item, index) => {
		const level = item.requirementLevel || "Unspecified";
		grouped[level].push({ ...item, _originalIndex: index }); // ✅ index réel
	  });
	  return grouped;
	};

  const groupedInventory = groupByRequirementLevel(inventory);
  let globalIndex = 0;

  return (
    <div className="inventory-wrapper">
      <div className="inventory-filters mb-4">
        <div className="inventory-filters-inner">
          <span className="inventory-title">Security Inventory - {selectedCountry}</span>
          <div className="inventory-actions">
            <select
              id="inventory-filter-country"
              name="affected_country"
              className="inventory-form-select"
              value={selectedCountry}
              onChange={handleCountryChange}
            >
              {countryOptions.map((country) => (
                <option key={country.code} value={country.code}>{country.name}</option>
              ))}
            </select>
            <button className="inventory-export-button" onClick={handleExportCSV}>
              Export CSV
            </button>
          </div>
        </div>
      </div>

      <div className="inventory-table-responsive">
        <table className="inventory-table">
          <thead className="inventory-thead">
            <tr>
              <th style={{ color: "rgb(248, 249, 250)", backgroundColor: "rgb(30, 30, 30)" }}>Category</th>
              <th style={{ color: "rgb(248, 249, 250)", backgroundColor: "rgb(30, 30, 30)" }}>Type</th>
              <th style={{ color: "rgb(248, 249, 250)", backgroundColor: "rgb(30, 30, 30)" }}>Solution Used</th>
              <th style={{ color: "rgb(248, 249, 250)", backgroundColor: "rgb(30, 30, 30)" }}>Provider / Vendor</th>
              <th style={{ color: "rgb(248, 249, 250)", backgroundColor: "rgb(30, 30, 30)" }}>Implementation Status</th>
            </tr>
          </thead>
          <tbody>

			{["Required", "Important", "Improvement", "Sustainability", "Unspecified"].map((level) => (
			  groupedInventory[level] && groupedInventory[level].length > 0 && (
				<React.Fragment key={level}>
				  <tr>
					<td colSpan="5" className={`inventory-group-header inventory-level-${level.toLowerCase().replace(/\s+/g, '-')}`}>{level}</td>
				  </tr>
				  {groupedInventory[level].map((item) => {
					const currentIndex = item._originalIndex;

					return (
					  <tr key={`${level}-${currentIndex}`}>
						<td>{item.category}</td>
						<td>{item.solutionType}</td>
						<td>
						  <input
							type="text"
							className="inventory-input"
							ref={(el) => inputRefs.current[`${currentIndex}-solutionUsed`] = el}
							value={item.solutionUsed || ''}
							disabled={!isEditableUser}
							onClick={() => unlockField(currentIndex, "solutionUsed")}
							onChange={(e) => handleChange(currentIndex, "solutionUsed", e.target.value)}
						  />
						</td>
						<td>
						  <input
							type="text"
							className="inventory-input"
							ref={(el) => inputRefs.current[`${currentIndex}-provider`] = el}
							value={item.provider || ''}
							disabled={!isEditableUser}
							onClick={() => unlockField(currentIndex, "provider")}
							onChange={(e) => handleChange(currentIndex, "provider", e.target.value)}
						  />
						</td>
						<td>
						  <select
							className={`inventory-select ${getStatusClass(item.implementationStatus)}`}
							value={item.implementationStatus}
							onChange={(e) => handleChange(currentIndex, "implementationStatus", e.target.value)}
							disabled={!isEditableUser}
						  >
							{implementationStatusOptions.map((status, idx) => (
							  <option key={idx} value={status}>{status}</option>
							))}
						  </select>
						</td>
					  </tr>
					);
				  })}
				</React.Fragment>
			  )
			))}
          </tbody>
        </table>
      </div>

      <ToastContainer position="bottom-end" className="p-3" style={{ position: "fixed", bottom: "20px", right: "20px", zIndex: 9999 }}>
        <Toast show={toast.show} bg={toast.variant} onClose={() => setToast(prev => ({ ...prev, show: false }))} delay={3000} autohide>
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
}
